/**
 * Convert a 2-letter ISO 3166-1 alpha-2 country code to a flag emoji.
 * Works by converting each letter to its corresponding Regional Indicator Symbol.
 * 
 * Note: Windows doesn't render flag emojis - use getFlagImageUrl() for cross-platform support.
 * 
 * Example: "US" -> 🇺🇸, "GB" -> 🇬🇧, "JP" -> 🇯🇵
 */
export function countryCodeToFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) {
    return '🏳️'; // Default white flag
  }
  
  const code = countryCode.toUpperCase();
  
  // Regional Indicator Symbol Letter A is at Unicode code point U+1F1E6
  // Each letter A-Z corresponds to code points U+1F1E6 to U+1F1FF
  const OFFSET = 127397; // This is 0x1F1E6 - 65 (ASCII 'A')
  
  const firstChar = code.charCodeAt(0);
  const secondChar = code.charCodeAt(1);
  
  // Validate that both characters are uppercase letters (A-Z)
  if (firstChar < 65 || firstChar > 90 || secondChar < 65 || secondChar > 90) {
    return '🏳️';
  }
  
  return String.fromCodePoint(firstChar + OFFSET, secondChar + OFFSET);
}

/**
 * Get flag image URL from flagcdn.com
 * Returns a URL to a flag image that works on all platforms including Windows
 */
export function getFlagImageUrl(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) {
    return ''; 
  }
  const code = countryCode.toLowerCase();
  // Using flagcdn.com - free CDN for flag images
  // w80 = width 80px, good quality for our use case
  return `https://flagcdn.com/w80/${code}.png`;
}

/**
 * Get flag emoji with fallback - uses provided flag or generates from code
 * Note: Flag emojis don't work on Windows - use getFlagImageUrl() for reliable display
 */
export function getFlag(flagEmoji: string | null | undefined, countryCode: string): string {
  if (flagEmoji && flagEmoji.trim()) {
    return flagEmoji;
  }
  return countryCodeToFlag(countryCode);
}
